/**
 * SignatureCapture — Shared signature widget (adopt/draw/type modes)
 * Extracted from PublicSigning.js for reuse in ClientStaging.js
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Pen, CheckCircle } from 'lucide-react';

const SignatureCapture = ({
  signerName = '',
  onSign,
  onDecline,
  showDecline = true,
  disabled = false,
  requirePin = false,
}) => {
  const canvasRef = useRef(null);
  const [signatureMode, setSignatureMode] = useState('adopt');
  const [isDrawing, setIsDrawing] = useState(false);
  const [typedSignature, setTypedSignature] = useState('');
  const [pin, setPin] = useState('');
  const [signing, setSigning] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  // Canvas coordinate helper
  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (e.touches) e.preventDefault();
    const { x, y } = getCanvasCoords(e, canvas);
    const ctx = canvas.getContext('2d');
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (e.touches) e.preventDefault();
    const { x, y } = getCanvasCoords(e, canvas);
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (e && e.touches) e.preventDefault();
    setIsDrawing(false);
  };

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const generateTypedSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !typedSignature) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = '48px "Brush Script MT", "Segoe Script", cursive';
    ctx.fillStyle = '#1a1a2e';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedSignature, 20, canvas.height / 2);
  }, [typedSignature]);

  const generateAdoptedSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signerName) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = '52px "Brush Script MT", "Segoe Script", "Apple Chancery", cursive';
    ctx.fillStyle = '#1a1a2e';
    ctx.textBaseline = 'middle';
    const metrics = ctx.measureText(signerName);
    const x = Math.max(20, (canvas.width - metrics.width) / 2);
    ctx.fillText(signerName, x, canvas.height / 2);
  }, [signerName]);

  useEffect(() => {
    if (signatureMode === 'type' && typedSignature) {
      generateTypedSignature();
    }
    if (signatureMode === 'adopt') {
      generateAdoptedSignature();
    }
  }, [typedSignature, signatureMode, signerName, generateTypedSignature, generateAdoptedSignature]);

  const isCanvasEmpty = () => {
    const canvas = canvasRef.current;
    if (!canvas) return true;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i] < 250 || pixels[i + 1] < 250 || pixels[i + 2] < 250) {
        return false;
      }
    }
    return true;
  };

  const handleSign = async () => {
    if (isCanvasEmpty()) {
      alert('Please provide your signature');
      return;
    }
    if (requirePin && !pin) {
      alert('Please enter your PIN');
      return;
    }
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    const method = signatureMode === 'draw' ? 'drawn' : signatureMode === 'adopt' ? 'adopted' : 'typed';

    setSigning(true);
    try {
      await onSign(signatureData, method, pin || null);
    } catch (err) {
      alert(err.message || 'Failed to apply signature');
    } finally {
      setSigning(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining');
      return;
    }
    if (!window.confirm('Are you sure you want to decline signing this document?')) {
      return;
    }
    setSigning(true);
    try {
      await onDecline(declineReason, pin || null);
    } catch (err) {
      alert(err.message || 'Failed to decline');
    } finally {
      setSigning(false);
    }
  };

  if (showDeclineForm) {
    return (
      <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Decline to Sign</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-virgo-teal-200 mb-2">
            Reason for declining *
          </label>
          <textarea
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            rows={4}
            placeholder="Please explain why you are declining to sign this document..."
            className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
            required
          />
        </div>
        {requirePin && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-virgo-teal-200 mb-2">PIN (Required)</label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter your PIN"
              className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
            />
          </div>
        )}
        <div className="flex gap-4">
          <button
            onClick={handleDecline}
            disabled={signing}
            className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {signing ? 'Processing...' : 'Confirm Decline'}
          </button>
          <button
            onClick={() => { setShowDeclineForm(false); setDeclineReason(''); }}
            disabled={signing}
            className="px-6 py-3 bg-virgo-navy-700 text-virgo-teal-200 rounded-lg hover:bg-virgo-navy-600 disabled:opacity-50 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Signing Instructions */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg flex-shrink-0">
            <Pen className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-amber-300 font-semibold mb-1">How to Sign</h3>
            <ol className="text-amber-200/80 text-sm space-y-1 list-decimal list-inside">
              <li>Review the document above carefully</li>
              <li>Adopt the generated signature below, draw your own, or type your name</li>
              <li>Click "Sign Document" to complete your electronic signature</li>
            </ol>
            <p className="text-amber-200/60 text-xs mt-2">
              Your electronic signature is legally binding and will be applied to the document.
            </p>
          </div>
        </div>
      </div>

      {/* Signature Section */}
      <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-white mb-4">Your Signature</h2>

        {/* Signature Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setSignatureMode('adopt')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium ${
              signatureMode === 'adopt'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-700'
            }`}
          >
            Adopt Signature
          </button>
          <button
            onClick={() => { setSignatureMode('draw'); clearSignature(); }}
            className={`flex-1 px-4 py-2 rounded-lg font-medium ${
              signatureMode === 'draw'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-700'
            }`}
          >
            Draw
          </button>
          <button
            onClick={() => { setSignatureMode('type'); clearSignature(); }}
            className={`flex-1 px-4 py-2 rounded-lg font-medium ${
              signatureMode === 'type'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-800 text-virgo-teal-200 hover:bg-virgo-navy-700'
            }`}
          >
            Type Name
          </button>
        </div>

        {signatureMode === 'adopt' && (
          <div className="mb-4 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
            <p className="text-green-300 text-sm">
              Your signature has been generated from your name on file. Review it below and click <strong>"Sign Document"</strong> to adopt it as your electronic signature.
            </p>
          </div>
        )}

        {signatureMode === 'type' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-virgo-teal-200 mb-2">Type your full name</label>
            <input
              type="text"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Signature Canvas */}
        <div className="border-2 border-virgo-teal-700 rounded-lg p-2 bg-virgo-navy-700 mb-4">
          <canvas
            ref={canvasRef}
            width={700}
            height={200}
            onMouseDown={signatureMode === 'draw' ? startDrawing : undefined}
            onMouseMove={signatureMode === 'draw' ? draw : undefined}
            onMouseUp={signatureMode === 'draw' ? stopDrawing : undefined}
            onMouseLeave={signatureMode === 'draw' ? stopDrawing : undefined}
            onTouchStart={signatureMode === 'draw' ? startDrawing : undefined}
            onTouchMove={signatureMode === 'draw' ? draw : undefined}
            onTouchEnd={signatureMode === 'draw' ? stopDrawing : undefined}
            className="w-full cursor-crosshair rounded"
            style={{ touchAction: 'none', backgroundColor: '#ffffff' }}
          />
        </div>

        {signatureMode !== 'adopt' && (
          <button
            onClick={clearSignature}
            className="text-sm text-virgo-teal-400 hover:text-virgo-teal-300 font-medium"
          >
            Clear Signature
          </button>
        )}

        {requirePin && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-virgo-teal-200 mb-2">PIN (Required)</label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter your PIN"
              className="w-full px-4 py-2 border border-virgo-teal-700 rounded-lg focus:ring-2 focus:ring-virgo-teal-500 focus:border-blue-500"
            />
          </div>
        )}

        {/* Signer Info */}
        {signerName && (
          <div className="mt-6 p-4 bg-virgo-navy-600 rounded-lg">
            <div className="text-sm text-virgo-teal-300">
              <p><strong>Signer:</strong> {signerName}</p>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-virgo-navy-700 rounded-lg shadow-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleSign}
            disabled={signing || disabled}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {signing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Processing...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Sign Document
              </>
            )}
          </button>

          {showDecline && (
            <button
              onClick={() => setShowDeclineForm(true)}
              disabled={signing || disabled}
              className="px-6 py-3 bg-virgo-navy-700 text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Decline to Sign
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignatureCapture;
